// ReportHeader.tsx
import IconLeftToolbar from "@/components/icons/IconLeftToolbar";
import image from "@/constants/image";
import { useSubscription } from "@/helper/useSubscription";
import useDeviceDetect from "@/hooks/useMobile";
import { setLoadingPdf } from "@/lib/features/statusSlice";
import { OPTIONS_SUBSCRIPTION } from "@/models/subscription";
import { User } from "@/models/user";
import ReportServices from "@/services/main/reportServices";
import { useAppDispatch, useAppSelector } from "@lib/hooks";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ShareIcon from "@mui/icons-material/Share";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import _ from "lodash";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import React, { memo, useCallback, useMemo, useState } from "react";
interface ReportHeaderProps {
  reportContentState?: any;
  isLogin?: boolean;
  reportId?: number;
  reportAttachments?: any;
  currentPage?: number;
  totalPages?: number;
  initialBookmarkOpen?: boolean;
  initialToolbarOpen?: boolean;
  onPageChange?: (page: number) => void;
  onFullscreen?: () => void;
  onShare?: () => void;
  onBookmark?: (isBookmarked: boolean) => void;
  onToolbarToggle?: (isOpen: boolean) => void;
  isShare?: boolean;
  previews?: any;
  isPayment?: boolean;
  reportType?: number;
  rightRef?: React.RefObject<{
    zoomIn: () => void;
    zoomOut: () => void;
    resetZoom: () => void;
    wheelZoom: (e: WheelEvent) => void;
  } | null>;
  zoomPercentage?: number;
  showZoomIndicator?: boolean;
  isFullscreen?: boolean;
  reportAttachmentsOption?: string[];
  slug?: string;
  lang?: string;
  user?: User | null;
}

const montserrat = Montserrat({
  subsets: ["latin"],
});

const ReportHeader = ({
  isLogin = false,
  user,
  reportId = 0,
  reportAttachments,
  currentPage = 0,
  totalPages = 0,
  initialBookmarkOpen = false,
  initialToolbarOpen = false,
  onPageChange,
  onFullscreen,
  onShare,
  onBookmark,
  onToolbarToggle,
  isShare,
  previews,
  isPayment,
  reportType,
  zoomPercentage,
  rightRef,
  showZoomIndicator,
  isFullscreen,
  slug,
  lang,
  reportAttachmentsOption,
}: ReportHeaderProps) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const handleClickChangeLang = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseChangeLang = () => {
    setAnchorEl(null);
  };
  const { hasPermission } = useSubscription(user?.license);
  const t = useTranslations("messages");
  const isMobile = useDeviceDetect();
  const { countBookmark, allowShareReport } = useAppSelector(
    (state) => state.report
  );
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarkOpen);
  const [isToolbarOpen, setIsToolbarOpen] = useState(initialToolbarOpen);
  // const [isMounted, setIsMounted] = useState<boolean>(false);
  // useEffect(() => {
  //   setIsMounted(true);
  // }, []);
  const handleToolbarToggle = () => {
    const newState = !isToolbarOpen;
    setIsToolbarOpen(newState);
    onToolbarToggle?.(newState);
  };
  const handleFullscreen = () => {
    onFullscreen?.();
  };

  const handleShare = useCallback(() => {
    onShare?.();
  }, [onShare]);

  const handleBookmark = () => {
    const newState = !isBookmarked;
    setIsBookmarked(newState);
    onBookmark?.(newState);
  };
  const handleCheckDisableLang = useMemo(() => {
    return (lang: string) => {
      return !reportAttachmentsOption?.some((option: string) => {
        return option === lang;
      });
    };
  }, [reportAttachmentsOption]);
  const mutationChangePdfLang = useMutation({
    mutationFn: async (newLang: string) => {
      const result = await ReportServices.getReportBySlug(slug, newLang);
      return result.data;
    },
    onSuccess: async (data: any) => {
      handleCloseChangeLang();
      await queryClient.setQueryData(
        ["report", slug, lang],
        (oldData: Report | undefined) => {
          return {
            ...oldData,
            reportAttachments: data?.reportAttachments,
          };
        }
      );
      await new Promise((r) => setTimeout(r, 1000));
      dispatch(setLoadingPdf(false));
    },
    onError: () => {
      dispatch(setLoadingPdf(false));
    },
  });
  const handleChange = (newLang: string) => {
    const currentLangReport = reportAttachments?.language || "en";
    if (currentLangReport === newLang) {
      return;
    }
    dispatch(setLoadingPdf(true));
    mutationChangePdfLang.mutate(newLang);
  };
  return (
    <Box className="py-2 md:py-3 !card-glass-effect-1 w-full z-[13] bg-white relative">
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        disableScrollLock={true}
        open={open}
        onClose={handleCloseChangeLang}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          list: {
            "aria-labelledby": "basic-button",
          },
        }}
        sx={{
          "& .MuiMenuItem-root": {
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "24px",
            letterSpacing: "0%",

            "&.Mui-selected": {
              backgroundColor: "var(--cimigo-blue)  !important",
              color: "#FFFFFF !important",
              "&:hover": {
                backgroundColor: "#4A7EBD !important",
              },
            },
          },
        }}
      >
        <MenuItem
          selected={(reportAttachments?.language || "en") === "en"}
          onClick={() => handleChange("en")}
        >
          English
        </MenuItem>
        <MenuItem
          selected={(reportAttachments?.language || "en") === "vi"}
          onClick={() => handleChange("vi")}
          disabled={handleCheckDisableLang("vi")}
        >
          Vietnamese
        </MenuItem>
        <MenuItem
          selected={(reportAttachments?.language || "en") === "id"}
          onClick={() => handleChange("id")}
          disabled={handleCheckDisableLang("id")}
        >
          Bahasa
        </MenuItem>
      </Menu>
      <Box className="px-4 flex md:px-12 sm:grid sm:grid-cols-3 items-center gap-2 sm:gap-0">
        {/* Left */}
        <Box className="justify-self-start w-full sm:w-auto flex justify-start">
          <Tooltip
            title={
              <span
                translation-key="report_header_toggle_toolbar_tooltip"
                className={montserrat.className}
              >
                {t("report_header_toggle_toolbar_tooltip")}
              </span>
            }
            placement="top"
            disableInteractive
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: { offset: [0, -14] },
                  },
                ],
              },
            }}
          >
            <IconButton
              className="w-8 h-8 md:w-10 md:h-10"
              onClick={handleToolbarToggle}
              aria-label={t("report_header_toggle_toolbar")}
            >
              <IconLeftToolbar className="!w-8 !h-8 md:!w-10 md:!h-10 text-gray-80" />
            </IconButton>
          </Tooltip>
        </Box>
        {/* Center */}
        <Box className="justify-self-center w-full sm:w-auto flex justify-center">
          <h1
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              fontSize: "13px",
              lineHeight: "24px",
              letterSpacing: "0%",
            }}
            translation-key="common_sample_preview"
            className="text-sm md:text-base"
          >
            {isPayment || hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM)
              ? t("report_header_page_indicator", {
                  current: currentPage,
                  total: previews?.length,
                })
              : t("common_sample_preview")}
          </h1>
        </Box>
        {/* Right */}
        <Box className="justify-self-end w-full sm:w-auto flex justify-end gap-1 md:gap-2 items-center relative">
          {/* Fullscreen and Zoom buttons */}
          {!isMobile && (
            <>
              {/* Fullscreen button */}
              <Tooltip
                title={
                  <span
                    translation-key="report_header_fullscreen_tooltip"
                    className={montserrat.className}
                  >
                    {t("report_header_fullscreen_tooltip")}
                  </span>
                }
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, -14] },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  className="w-8 h-8 md:w-10 md:h-10"
                  onClick={handleFullscreen}
                  aria-label={t("report_header_fullscreen")}
                >
                  <Image
                    src={image.iconFullscreen}
                    alt={t("report_header_fullscreen")}
                    className="!w-5 !h-5 md:!w-6 md:!h-6 text-gray-80"
                  />
                </IconButton>
              </Tooltip>
            </>
          )}

          {!isShare && (
            <Tooltip
              title={
                <span
                  translation-key="report_header_change_language_tooltip"
                  className={montserrat.className}
                >
                  {t("report_header_change_language_tooltip")}
                </span>
              }
              placement="top"
              disableInteractive
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: { offset: [0, -14] },
                    },
                  ],
                },
              }}
            >
              <IconButton
                className="w-8 h-8 md:w-10 md:h-10"
                onClick={handleClickChangeLang}
                aria-label={t("report_header_share")}
              >
                <TranslateIcon className="!w-5 !h-5 md:!w-6 md:!h-6 text-gray-80" />
              </IconButton>
            </Tooltip>
          )}
          {!isShare && isLogin && (
            <>
              {(allowShareReport ||
                hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM)) && (
                <Tooltip
                  title={
                    <span
                      translation-key="report_header_share_tooltip"
                      className={montserrat.className}
                    >
                      {t("report_header_share_tooltip")}
                    </span>
                  }
                  placement="top"
                  disableInteractive
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: { offset: [0, -14] },
                        },
                      ],
                    },
                  }}
                >
                  <IconButton
                    className="w-8 h-8 md:w-10 md:h-10"
                    onClick={handleShare}
                    aria-label={t("report_header_share")}
                  >
                    <ShareIcon className="!w-5 !h-5 md:!w-6 md:!h-6 text-gray-80" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={
                  <span
                    translation-key="report_header_bookmark_tooltip"
                    className={montserrat.className}
                  >
                    {t("report_header_bookmark_tooltip")}
                  </span>
                }
                placement="top"
                disableInteractive
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: { offset: [0, -14] },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  className={`!w-[48px] !h-[36px] md:!w-[58px] md:!h-[40px] !rounded-[4px] !p-[0px] md:!p-[8px] ${isBookmarked ? "!bg-gray_5" : ""}`}
                  onClick={handleBookmark}
                  aria-label={t("report_header_bookmark")}
                >
                  {countBookmark[reportId] ? (
                    <BookmarkAddedIcon className="!w-5 !h-5 md:!w-6 md:!h-6 text-gray-80" />
                  ) : (
                    <BookmarkBorderIcon className="!w-5 !h-5 md:!w-6 md:!h-6 text-gray-80" />
                  )}
                  <Typography
                    className={`!ml-1 md:!ml-2 text-gray-80 text-sm md:text-base ${isBookmarked ? "font-bold" : "font-normal"}`}
                  >
                    {countBookmark[reportId] || 0}
                  </Typography>
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
      <AnimatePresence mode="wait">
        {!isFullscreen && showZoomIndicator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="!absolute !top-16 !right-56 !z-50 flex flex-col items-center gap-2"
          >
            <Tooltip
              title={
                <span
                  translation-key="report_content_zoom_info"
                  className={montserrat.className}
                >
                  {t("report_content_zoom_info")}
                </span>
              }
              placement="right"
            >
              <Box
                className="!bg-white !shadow-md !rounded-b-md !px-3 !py-1 !text-sm"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                  opacity: 0.8,
                }}
              >
                {zoomPercentage}%
              </Box>
            </Tooltip>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default memo(ReportHeader, (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
});
